import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const withLocalParam = (path, searchParams) => {
  const local =
    searchParams.get('local') || searchParams.get('locale') || searchParams.get('country');
  if (!local) return path;
  const next = new URLSearchParams();
  next.set('local', local);
  return `${path}?${next.toString()}`;
};

const NAV_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/pricing', label: 'Pricing' },
];

/**
 * @param {'dark' | 'light'} variant
 * dark = hero overlay (sand text), light = solid sand pages
 */
const MarketingNav = ({ variant = 'light', current }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const activePath = current || location.pathname;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  const isDark = variant === 'dark';

  const shell = isDark
    ? 'absolute inset-x-0 top-0 z-30'
    : 'relative z-30 border-b border-ink-900/10 bg-sand-100';

  const brandText = isDark ? 'text-sand-50' : 'text-ink-900';
  const logoRing = isDark ? 'ring-sand-100/30' : 'ring-ink-900/15';
  const linkIdle = isDark
    ? 'text-sand-100/80 hover:text-sand-50'
    : 'text-ink-700 hover:text-ink-900';
  const linkActive = isDark ? 'text-sand-50' : 'text-ink-900';
  const loginClass = isDark
    ? 'px-3 py-2 text-sm font-medium text-sand-100/80 transition-colors hover:text-sand-50'
    : 'px-3 py-2 text-sm font-medium text-ink-700 transition-colors hover:text-ink-900';
  const signupClass = isDark
    ? 'bg-sand-50/10 px-4 py-2.5 text-sm font-semibold text-sand-50 ring-1 ring-inset ring-sand-100/30 backdrop-blur-sm transition-colors hover:bg-sand-50/20'
    : 'bg-ink-900 px-4 py-2.5 text-sm font-semibold text-sand-50 transition-colors hover:bg-ink-800';
  const menuBtnClass = isDark
    ? 'inline-flex items-center justify-center p-2 text-sand-50 ring-1 ring-sand-100/30 md:hidden'
    : 'inline-flex items-center justify-center p-2 text-ink-900 ring-1 ring-ink-900/15 md:hidden';
  const panelClass = isDark
    ? 'border-t border-sand-100/15 bg-ink-950/95 backdrop-blur-md md:hidden'
    : 'border-t border-ink-900/10 bg-sand-50 md:hidden';

  return (
    <nav className={shell}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-6 sm:py-6 lg:px-8">
        <Link
          to={withLocalParam('/', searchParams)}
          className="flex min-w-0 items-center gap-2.5 sm:gap-3"
        >
          <div className={`h-10 w-10 flex-none overflow-hidden rounded-full ring-1 ${logoRing} sm:h-11 sm:w-11`}>
            <img src="/logo192.png" alt="" className="h-full w-full scale-110 object-cover" />
          </div>
          <span
            className={`font-display text-xl font-light tracking-tight sm:text-2xl ${brandText}`}
          >
            Spirara<span className="text-saffron-500">.</span>
          </span>
        </Link>

        {/* Desktop */}
        <div className="hidden items-center gap-5 md:flex lg:gap-6">
          {NAV_LINKS.map((item) => {
            const isActive = activePath === item.to;
            return (
              <Link
                key={item.to}
                to={withLocalParam(item.to, searchParams)}
                className={`text-sm transition-colors ${
                  isActive ? `font-semibold ${linkActive}` : `font-medium ${linkIdle}`
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                {item.label}
              </Link>
            );
          })}
          <Link to={withLocalParam('/login', searchParams)} className={loginClass}>
            Log in
          </Link>
          <Link to={withLocalParam('/signup', searchParams)} className={signupClass}>
            Sign up
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className={menuBtnClass}
          aria-expanded={open}
          aria-controls="marketing-mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div id="marketing-mobile-menu" className={panelClass}>
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4 sm:px-6">
            {NAV_LINKS.map((item) => {
              const isActive = activePath === item.to;
              return (
                <Link
                  key={item.to}
                  to={withLocalParam(item.to, searchParams)}
                  className={`px-1 py-3 text-base transition-colors ${
                    isActive ? `font-semibold ${linkActive}` : `font-medium ${linkIdle}`
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              );
            })}

            <Link
              to={withLocalParam('/login', searchParams)}
              className={`px-1 py-3 text-base font-medium ${linkIdle}`}
            >
              Log in
            </Link>
            <Link
              to={withLocalParam('/signup', searchParams)}
              className={`mt-2 inline-flex items-center justify-center ${signupClass}`}
            >
              Sign up
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default MarketingNav;
